/**
 * @file The application's navigation map.
 *
 * One place that answers "what pages does this product have and how are they
 * grouped", so the sidebar, the router and any future command palette cannot
 * disagree. Categories and pages come from the data model and task queue in
 * PLAN.md §5/§10 — not from invention — and anything not yet built is marked
 * `soon` rather than omitted, because the shape of the product is useful to see
 * and a landlord's mental model is "properties, leases, money, maintenance".
 *
 * ## `soon` items are NOT links
 *
 * A nav full of items that 404 is worse than no nav: the first click teaches the
 * user the app is broken. `soon` entries render as plain, visibly-disabled text
 * with a "Soon" badge, and there is no route behind them. When a page ships it
 * flips to `live` and gains a `to`, and the type checker requires the route to
 * exist because `to` is typed against the router's route tree.
 *
 * **As of Account & team, there are zero `soon` items left** — every page in this
 * tree is a real, clickable destination. The `NavSoon` type and the sidebar branch
 * that renders it are kept, deliberately; see the type's own comment for why.
 *
 * ## What is deliberately absent
 *
 * No "Applications" or "Screening" page: screening is referral-only until an FCRA
 * program exists, and a nav item would imply the capability (CLAUDE.md, product
 * law). No "Legal notices" or "Evictions" either, for the same reason. The nav is
 * a product decision surface, not just a menu.
 */
import {
  Banknote,
  Building2,
  CalendarClock,
  History,
  FileBarChart,
  Gauge,
  Hammer,
  Inbox,
  Receipt,
  Repeat2,
  ScrollText,
  Settings,
  Users,
  UsersRound,
  Wallet,
} from "lucide-react";
import type { ComponentType } from "react";

/** A destination that exists. `to` is a literal route path the router knows. */
export interface NavLink {
  readonly kind: "link";
  readonly label: string;
  readonly icon: ComponentType<{ readonly className?: string }>;
  /**
   * The route path, as a literal — deliberately not built by string
   * concatenation. TanStack Router types `to` against its route tree (and
   * enables type-checked `params` with it), and a template string erases that to
   * `string`, which would silently accept a path that does not exist.
   */
  readonly to:
    | "/workspaces/$workspaceId"
    | "/workspaces/$workspaceId/properties"
    | "/workspaces/$workspaceId/units"
    | "/workspaces/$workspaceId/leases"
    | "/workspaces/$workspaceId/tenants"
    | "/workspaces/$workspaceId/guarantors"
    | "/workspaces/$workspaceId/ledger"
    | "/workspaces/$workspaceId/rent-charges"
    | "/workspaces/$workspaceId/payments"
    | "/workspaces/$workspaceId/expenses"
    | "/workspaces/$workspaceId/maintenance"
    | "/workspaces/$workspaceId/turnover"
    | "/workspaces/$workspaceId/reports"
    | "/workspaces/$workspaceId/account"
    | "/workspaces/$workspaceId/activity";
}

/**
 * A destination that does not exist yet. Rendered disabled, never clickable.
 *
 * **Kept deliberately, though nothing uses it today.** With Account & team live,
 * `NAV_GROUPS` contains zero `soon` items, so the branch in `AppSidebar` that
 * renders this is unreachable and every page in the product is now a real link.
 *
 * The alternative was to delete the type and the branch, on the grounds that dead
 * code should not be carried. That was rejected, and the reason is the one this
 * file's header already gives: `soon` is not a placeholder mechanism, it is the
 * product's way of showing the shape of a product it does not have yet without
 * teaching a landlord that the app is broken. PLAN.md §10 still has unbuilt
 * surfaces that ARE user-visible (the tenant portal at T1.7 is the obvious one),
 * and the next nav item will need exactly this branch. Deleting it would make the
 * next `soon` item an exercise in rediscovering why the `Soon` badge exists —
 * which is how a nav item ends up shipped as a 404 link instead.
 *
 * What must NOT happen is an unused `soon` entry appearing in the tree to justify
 * the type. The type is a capability; `NAV_GROUPS` is the honesty. Today the tree
 * has zero of them, and `nav.test.ts` asserts it.
 */
export interface NavSoon {
  readonly kind: "soon";
  readonly label: string;
  readonly icon: ComponentType<{ readonly className?: string }>;
}

export type NavItem = NavLink | NavSoon;

export interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/**
 * The tree, grouped the way a landlord thinks about the work rather than the way
 * the tables are organised. Overview first; Money before Operations because it is
 * the thing a landlord opens the app for.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Overview",
    items: [
      { kind: "link", label: "Dashboard", icon: Gauge, to: "/workspaces/$workspaceId" },
    ],
  },
  {
    label: "Portfolio",
    items: [
      {
        kind: "link",
        label: "Properties",
        icon: Building2,
        to: "/workspaces/$workspaceId/properties",
      },
      { kind: "link", label: "Units & beds", icon: Inbox, to: "/workspaces/$workspaceId/units" },
    ],
  },
  {
    label: "Leasing",
    items: [
      {
        kind: "link",
        label: "Leases",
        icon: ScrollText,
        to: "/workspaces/$workspaceId/leases",
      },
      { kind: "link", label: "Tenants", icon: Users, to: "/workspaces/$workspaceId/tenants" },
      {
        kind: "link",
        label: "Guarantors",
        icon: UsersRound,
        to: "/workspaces/$workspaceId/guarantors",
      },
      {
        kind: "link",
        label: "Turnover",
        icon: Repeat2,
        to: "/workspaces/$workspaceId/turnover",
      },
    ],
  },
  {
    label: "Money",
    items: [
      {
        kind: "link",
        label: "Rent & charges",
        icon: CalendarClock,
        to: "/workspaces/$workspaceId/rent-charges",
      },
      { kind: "link", label: "Payments", icon: Banknote, to: "/workspaces/$workspaceId/payments" },
      { kind: "link", label: "Ledger", icon: Wallet, to: "/workspaces/$workspaceId/ledger" },
      { kind: "link", label: "Expenses", icon: Receipt, to: "/workspaces/$workspaceId/expenses" },
      {
        kind: "link",
        label: "Reports",
        icon: FileBarChart,
        to: "/workspaces/$workspaceId/reports",
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        kind: "link",
        label: "Maintenance",
        icon: Hammer,
        to: "/workspaces/$workspaceId/maintenance",
      },
    ],
  },
  {
    label: "Settings",
    items: [
      {
        kind: "link",
        label: "Account & team",
        icon: Settings,
        to: "/workspaces/$workspaceId/account",
      },
      {
        kind: "link",
        label: "Activity",
        icon: History,
        to: "/workspaces/$workspaceId/activity",
      },
    ],
  },
];

/** Every live destination, for tests and for a future command palette. */
export const LIVE_NAV_ITEMS: readonly NavLink[] = NAV_GROUPS.flatMap((group) =>
  group.items.filter((item): item is NavLink => item.kind === "link"),
);
